import type { Page } from '../../crm/client/pager'
import { toItem, type VaultItem } from '../../library/vault/vaultData'
import { applyEditPatch, buildPutPostBody, type EditPostPatch } from '../editPost'
import { defaultScheduleTime } from '../home/homeData'
import type { OfApi } from '../ofApi'
import { toPost, type Post } from '../posts'
import { dayInZone, SCHEDULE_TYPES, type ScheduleType, type Ymd } from './queueDates'

/**
 * OnlyFans queue payloads → what the Queue shows (live shapes 2026-10-08,
 * SHAPES.md: schedules, schedules/counters, schedules/later/*, users/settings/*).
 * Home's "Scheduled events" card reads the same `schedules` call and can use
 * toScheduleItem + groupByDay.
 */

type Raw = Record<string, unknown>

const num = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) ? v : 0)
const str = (v: unknown) => (typeof v === 'string' ? v : '')

export interface ScheduleItem {
  /** The SCHEDULE id (Publish now takes this, not the post id). */
  id: number
  type: ScheduleType
  /** ISO; null for saved-for-later items. */
  publishAt: string | null
  createdAt: string
  /** Saved for later: goes out with the auto-publish period, not at a date. */
  later: boolean
  /** The post entity; null for messages and streams (shape not captured). */
  post: Post | null
  /** OnlyFans HTML. */
  text: string
  expiresAt: string | null
  media: VaultItem[]
  mediaCount: number
}

function asType(v: unknown): ScheduleType {
  return v === 'chat' || v === 'stream' ? v : 'post'
}

export function toScheduleItem(raw: Raw): ScheduleItem {
  const type = asType(raw.type)
  const entity = (raw.entity ?? {}) as Raw
  const post = type === 'post' ? toPost(entity) : null
  const media = post?.media ?? (Array.isArray(entity.media) ? (entity.media as Raw[]).map(toItem) : [])
  const publishAt = str(raw.publishDateTime) || null
  return {
    id: num(raw.id),
    type,
    publishAt,
    createdAt: str(raw.createdDateTime),
    later: publishAt === null,
    post,
    text: str(entity.text),
    expiresAt: str(entity.expiredAt) || null,
    media,
    mediaCount: num(entity.mediaCount) || media.length,
  }
}

export type DayCount = Record<ScheduleType, number> & { total: number }

/** `{list:{'2026-10-08':{post:148}}}` → per-day counts; an empty month is `list: []`. */
export function toCounters(payload: Raw | null | undefined): Record<Ymd, DayCount> {
  const list = payload?.list
  const out: Record<Ymd, DayCount> = {}
  if (!list || Array.isArray(list) || typeof list !== 'object') return out
  for (const [day, raw] of Object.entries(list as Record<string, Raw>)) {
    const count = { post: num(raw?.post), chat: num(raw?.chat), stream: num(raw?.stream), total: 0 }
    count.total = SCHEDULE_TYPES.reduce((n, t) => n + count[t], 0)
    if (count.total > 0) out[day] = count
  }
  return out
}

export interface DayGroup {
  day: Ymd
  items: ScheduleItem[]
}

/** Scheduled items by their day in `timeZone`, days and items in time order. */
export function groupByDay(items: readonly ScheduleItem[], timeZone: string): DayGroup[] {
  const sorted = items.filter((i) => i.publishAt).sort((a, b) => Date.parse(a.publishAt!) - Date.parse(b.publishAt!))
  const groups: DayGroup[] = []
  for (const item of sorted) {
    const day = dayInZone(item.publishAt!, timeZone)
    const last = groups[groups.length - 1]
    if (last?.day === day) last.items.push(item)
    else groups.push({ day, items: [item] })
  }
  return groups
}

/**
 * `PUT posts/<id>` for a queued post (SPEC §4): the create shape, and the
 * schedule kept — OnlyFans publishes a post edited without it.
 */
export function editBody(item: ScheduleItem, patch: EditPostPatch = {}): Raw {
  const post = item.post
  const base = {
    text: item.text,
    labels: post?.labels ?? [],
    media: patch.media ?? post?.media ?? item.media,
    price: patch.price ?? post?.price ?? 0,
    previewIds: post?.previewIds ?? [],
  }
  return buildPutPostBody(base, { ...patch, textHtml: patch.textHtml ?? item.text }, item.publishAt ? { at: item.publishAt } : { later: true })
}

/** Apply a successful queue edit to the list item (optimistic). */
export function applyQueueEdit(item: ScheduleItem, patch: EditPostPatch): ScheduleItem {
  const text = patch.textHtml ?? item.text
  const post = item.post ? applyEditPatch(item.post, patch) : null
  const media = patch.media ?? post?.media ?? item.media
  let expiresAt = item.expiresAt
  if (patch.expireDays === null) expiresAt = null
  else if (typeof patch.expireDays === 'number' && patch.expireDays > 0) {
    const from = Date.parse(item.publishAt ?? post?.postedAt ?? item.createdAt)
    expiresAt = Number.isFinite(from) ? new Date(from + patch.expireDays * 86_400_000).toISOString() : item.expiresAt
  }
  return {
    ...item,
    text,
    post: post && { ...post, text, expiredAt: expiresAt },
    media,
    mediaCount: media.length,
    expiresAt,
  }
}

export type { EditPostPatch }

export interface AutoPublish {
  /** Hours since the last post / message. */
  period: number
  enabled: boolean
  options: number[]
}

const DEFAULT_OPTIONS = [6, 12, 24, 48]

/** `users/settings/post|chat` → `{currentCode, isEnabled, options}`. */
export function toAutoPublish(raw: Raw | null | undefined): AutoPublish {
  const options = Array.isArray(raw?.options) ? (raw.options as unknown[]).map(num).filter((n) => n > 0) : []
  const opts = options.length ? options : DEFAULT_OPTIONS
  return { period: num(raw?.currentCode) || opts[0]!, enabled: raw?.isEnabled === true, options: opts }
}

/**
 * "+" Add to queue on a picked day: that day at the composer's default time
 * of day (the next whole five minutes, an hour on); never earlier than that.
 */
export function scheduleOn(day: Ymd, now = new Date()): Date {
  const soonest = defaultScheduleTime(now)
  const [y, m, d] = day.split('-').map(Number)
  const at = new Date(soonest)
  at.setFullYear(y!, m! - 1, d!)
  return at.getTime() > soonest.getTime() ? at : soonest
}

export type LaterKind = 'post' | 'chat'

const LATER_PAGE = 10

export const LATER_REFERER: Record<LaterKind, string> = {
  post: 'https://onlyfans.com/saved-for-later-posts',
  chat: 'https://onlyfans.com/saved-for-later-messages',
}

/** `GET schedules/later/post|chat?limit=10` (offset paging). */
export function laterFetcher(of: OfApi, kind: LaterKind) {
  return async (offset: number): Promise<Page<ScheduleItem>> => {
    const p = await of.get<{ list?: Raw[]; hasMore?: boolean }>(`schedules/later/${kind}`, { limit: LATER_PAGE, offset: offset || null }, LATER_REFERER[kind])
    return { list: (p?.list ?? []).map(toScheduleItem), hasMore: p?.hasMore === true }
  }
}
